import React, { useRef } from "react";
import * as XLSX from "xlsx";

import { FaDownload, FaRegFileAlt } from "react-icons/fa";
import { FaCircleNotch } from "react-icons/fa6";
import { FiAlertCircle } from 'react-icons/fi';
import { formatSalesValue } from "../../utils/formatUtils";

const ComplimentaryItemsTable = ({
    data,
    loading,
    error,
    startDate,
    endDate,
}) => {
    const tableRef = useRef(null);

    const isGuestSpecific = data?.mode === "guest_specific";

    const handleExport = () => {
        const rows = [];

        rows.push(["Complimentary Items Report"]);
        rows.push([`From: ${startDate}`]);
        rows.push([`To: ${endDate}`]);
        rows.push([]);
        rows.push([]);

        customers.forEach((customer) => {
            const foodTotal =
                Number(customer.type_summary?.Food?.total_amount) || 0;

            const beverageTotal =
                Number(customer.type_summary?.Beverage?.total_amount) || 0;

            const grandTotal = foodTotal + beverageTotal;

            rows.push([
                `${customer.GuestName} | Grand Total: ${grandTotal.toFixed(2)}`
            ]);
            rows.push([]);

            rows.push([
                "Food",
                "",
                "",
                "",
                "",
                "Beverage",
                "",
                "",
                "",
                "",
            ]);

            rows.push([
                "Item",
                "Group",
                "Qty",
                "Rate",
                "Amount",
                "Item",
                "Group",
                "Qty",
                "Rate",
                "Amount",
            ]);

            const foodItems =
                customer.items_by_type?.Food || [];

            const beverageItems =
                customer.items_by_type?.Beverage || [];

            const maxRows = Math.max(
                foodItems.length,
                beverageItems.length
            );

            for (let i = 0; i < maxRows; i++) {
                const food = foodItems[i] || {};
                const beverage = beverageItems[i] || {};

                rows.push([
                    food.ItemName || "",
                    food.Description || "",
                    food.total_count || "",
                    food.itemRate || "",
                    food.total_amount || "",

                    beverage.ItemName || "",
                    beverage.Description || "",
                    beverage.total_count || "",
                    beverage.itemRate || "",
                    beverage.total_amount || "",
                ]);
            }

            rows.push([
                "Total",
                "",
                customer.type_summary?.Food?.total_count || "",
                "",
                foodTotal || "",

                "Total",
                "",
                customer.type_summary?.Beverage?.total_count || "",
                "",
                beverageTotal || "",
            ]);

            rows.push([]);
            rows.push([]);
        });

        const ws = XLSX.utils.aoa_to_sheet(rows);

        ws["!cols"] = [
            { wch: 25 },
            { wch: 20 },
            { wch: 8 },
            { wch: 12 },
            { wch: 12 },

            { wch: 25 },
            { wch: 20 },
            { wch: 8 },
            { wch: 12 },
            { wch: 12 },
        ];

        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            wb,
            ws,
            "Complimentary Items"
        );

        XLSX.writeFile(
            wb,
            "complimentary_items.xlsx"
        );
    };

    const renderItemTable = (
        items = [],
        summary = null
    ) => {
        if (!items.length) {
            return (
                <div className="text-center text-gray-400 py-2">
                    No Items
                </div>
            );
        }

        return (
            <>
                <table className="w-full text-xs">
                    <thead>
                        <tr className="bg-gray-50">
                            <th className="border px-2 py-1">Item</th>
                            <th className="border px-2 py-1">Group</th>
                            <th className="border px-2 py-1">Qty</th>
                            <th className="border px-2 py-1">Rate</th>
                            <th className="border px-2 py-1">Total</th>
                        </tr>
                    </thead>

                    <tbody>
                        {items.map((item, index) => (
                            <tr key={index}>
                                <td className="border px-2 py-1">
                                    {item.ItemName}
                                </td>

                                <td className="border px-2 py-1">
                                    {item.Description}
                                </td>

                                <td className="border px-2 py-1 text-center">
                                    {item.total_count}
                                </td>

                                <td className="border px-2 py-1 text-right">
                                    {formatSalesValue(item.itemRate)}
                                </td>

                                <td className="border px-2 py-1 text-right">
                                    {formatSalesValue(item.total_amount)}
                                </td>
                            </tr>
                        ))}
                    </tbody>

                    {summary && (
                        <tfoot>
                            <tr className="bg-gray-100 font-semibold">
                                <td
                                    colSpan={2}
                                    className="border px-2 py-1 text-right"
                                >
                                    Total
                                </td>

                                <td className="border px-2 py-1 text-center">
                                    {summary.total_count}
                                </td>

                                <td className="border px-2 py-1"></td>

                                <td className="border px-2 py-1 text-right">
                                    {formatSalesValue(summary.total_amount)}
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </>
        );
    };

    const customers = data?.customers || [];

    const grandFoodQty = customers.reduce(
        (sum, c) =>
            sum +
            Number(
                c.type_summary?.Food?.total_count || 0
            ),
        0
    );

    const grandFoodAmount = customers.reduce(
        (sum, c) =>
            sum +
            Number(
                c.type_summary?.Food?.total_amount || 0
            ),
        0
    );

    const grandBeverageQty = customers.reduce(
        (sum, c) =>
            sum +
            Number(
                c.type_summary?.Beverage?.total_count || 0
            ),
        0
    );

    const grandBeverageAmount = customers.reduce(
        (sum, c) =>
            sum +
            Number(
                c.type_summary?.Beverage?.total_amount || 0
            ),
        0
    );

    if (error) {
        return (
            <div className="flex items-start justify-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 shadow-sm font-semibold">
                <FiAlertCircle className="h-6 w-6 flex-shrink-0" />

                <div>{error}</div>
            </div>
        )
    }

    return (
        <div className="flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Header */}

            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50">
                <div className="text-sm font-semibold text-gray-700 tracking-wide uppercase">
                    Complimentary Food & Beverage
                </div>

                <button
                    onClick={handleExport}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded transition-colors"
                >
                    <FaDownload size={12} />
                    Export
                </button>
            </div>

            <div className="overflow-auto max-h-[700px] tiny-scrollbar">
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-48 gap-3 text-gray-400">
                        <FaCircleNotch
                            size={28}
                            className="animate-spin text-green-500"
                        />

                        <span className="text-sm">
                            Loading complimentary items...
                        </span>
                    </div>
                ) : (
                    <>
                        {isGuestSpecific ? (
                            <>
                                {!data?.items_by_type ? (
                                    <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
                                        <FaRegFileAlt
                                            size={40}
                                            className="opacity-40"
                                        />

                                        <p className="text-sm">
                                            No complimentary items found
                                        </p>
                                    </div>
                                ) : (
                                    <table
                                        ref={tableRef}
                                        className="w-full border-collapse"
                                    >
                                        <thead>
                                            <tr className="bg-green-50 sticky top-0 z-10">
                                                <th className="border px-3 py-2">
                                                    Food
                                                </th>

                                                <th className="border px-3 py-2">
                                                    Beverage
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            <tr>
                                                <td className="align-top p-2">
                                                    {renderItemTable(
                                                        data.items_by_type?.Food || [],
                                                        data.type_summary?.Food
                                                    )}
                                                </td>

                                                <td className="align-top p-2">
                                                    {renderItemTable(
                                                        data.items_by_type?.Beverage || [],
                                                        data.type_summary?.Beverage
                                                    )}
                                                </td>
                                            </tr>
                                        </tbody>

                                        <tfoot>
                                            <tr className="bg-green-100 font-semibold">
                                                <td className="border px-3 py-2 text-center">
                                                    Qty: {data?.type_summary?.Food?.total_count || 0}
                                                    &nbsp;&nbsp;|&nbsp;&nbsp;
                                                    Amount: {formatSalesValue(data?.type_summary?.Food?.total_amount)}
                                                </td>
                                                <td className="border px-3 py-2 text-center">
                                                    Qty: {data?.type_summary?.Beverage?.total_count || 0}
                                                    &nbsp;&nbsp;|&nbsp;&nbsp;
                                                    Amount: {formatSalesValue(data?.type_summary?.Beverage?.total_amount)}
                                                </td>
                                            </tr>
                                            <tr className="bg-green-50 font-semibold">
                                                <td colSpan={2} className="border px-3 py-2 text-center">
                                                    Grand Total:&nbsp;
                                                    {formatSalesValue(
                                                        Number(data?.type_summary?.Food?.total_amount || 0) +
                                                        Number(data?.type_summary?.Beverage?.total_amount || 0)
                                                    )}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </>
                        ) : (
                            <>
                                {customers.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
                                        <FaRegFileAlt
                                            size={40}
                                            className="opacity-40"
                                        />

                                        <p className="text-sm">
                                            No complimentary items found
                                        </p>
                                    </div>
                                ) : (
                                    <table
                                        ref={tableRef}
                                        className="w-full border-collapse text-sm"
                                    >
                                        <thead>
                                            <tr className="bg-green-50 sticky top-0 z-10">
                                                <th className="border px-3 py-2">
                                                    Guest Name
                                                </th>

                                                <th className="border px-3 py-2">
                                                    Food
                                                </th>

                                                <th className="border px-3 py-2">
                                                    Beverage
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {customers.map((customer) => (
                                                <tr
                                                    key={customer.guestID}
                                                    className="border-b"
                                                >
                                                    <td className="align-top p-3 font-medium whitespace-nowrap">
                                                        {customer.GuestName}
                                                        <div className="text-sm text-gray-700 font-semibold mt-1.5">
                                                            (Total: {formatSalesValue(
                                                                Number(customer.type_summary?.Food?.total_amount || 0) +
                                                                Number(customer.type_summary?.Beverage?.total_amount || 0)
                                                            )})
                                                        </div>
                                                    </td>

                                                    <td className="align-top p-2">
                                                        {renderItemTable(
                                                            customer.items_by_type?.Food || [],
                                                            customer.type_summary?.Food
                                                        )}
                                                    </td>

                                                    <td className="align-top p-2">
                                                        {renderItemTable(
                                                            customer.items_by_type?.Beverage || [],
                                                            customer.type_summary?.Beverage
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>

                                        <tfoot>
                                            <tr className="bg-green-100 font-semibold">
                                                <td className="border px-3 py-2">
                                                    Grand Total: {formatSalesValue(grandBeverageAmount + grandFoodAmount)}
                                                </td>

                                                <td className="border px-3 py-2">
                                                    Qty: {grandFoodQty}
                                                    &nbsp;&nbsp;|&nbsp;&nbsp;
                                                    Amount: {formatSalesValue(grandFoodAmount)}
                                                </td>

                                                <td className="border px-3 py-2">
                                                    Qty: {grandBeverageQty}
                                                    &nbsp;&nbsp;|&nbsp;&nbsp;
                                                    Amount: {formatSalesValue(grandBeverageAmount)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ComplimentaryItemsTable;